import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiPatch, apiPost, apiDelete, operationsClient } from '@/lib/api';
import { emailTemplateEndpoints } from '@/lib/api/endpoints';
import { emailTemplateKeys } from '@/lib/queryKeys';
import type {
  CreateEmailTemplateDto,
  EmailTemplateFull,
  UpdateEmailTemplateDto,
} from '@/types/emailTemplate';

const invalidateLists = (queryClient: QueryClient) =>
  void queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });

const invalidateDetail = (queryClient: QueryClient, id: string) =>
  void queryClient.invalidateQueries({ queryKey: emailTemplateKeys.detail(id) });

export const useCreateEmailTemplate = createMutationHook<EmailTemplateFull, CreateEmailTemplateDto>(
  {
    mutationFn: (dto) =>
      apiPost<EmailTemplateFull, CreateEmailTemplateDto>(
        operationsClient,
        emailTemplateEndpoints.create(),
        dto,
      ),
    onSuccess: (data, _vars, queryClient) => {
      invalidateLists(queryClient);
      invalidateDetail(queryClient, data.id);
      toast.success('Email created');
    },
  },
);

export const useUpdateEmailTemplate = createMutationHook<
  EmailTemplateFull,
  { id: string; dto: UpdateEmailTemplateDto }
>({
  mutationFn: ({ id, dto }) =>
    apiPatch<EmailTemplateFull, UpdateEmailTemplateDto>(
      operationsClient,
      emailTemplateEndpoints.update(id),
      dto,
    ),
  onSuccess: (_data, { id }, queryClient) => {
    invalidateLists(queryClient);
    invalidateDetail(queryClient, id);
    toast.success('Email saved');
  },
});

export const usePublishEmailTemplate = createMutationHook<EmailTemplateFull, { id: string }>({
  mutationFn: ({ id }) =>
    apiPost<EmailTemplateFull, undefined>(operationsClient, emailTemplateEndpoints.publish(id)),
  onSuccess: (_data, { id }, queryClient) => {
    invalidateLists(queryClient);
    invalidateDetail(queryClient, id);
    toast.success('Email published');
  },
});

export const useSetDefaultEmailTemplate = createMutationHook<EmailTemplateFull, { id: string }>({
  mutationFn: ({ id }) =>
    apiPost<EmailTemplateFull, undefined>(operationsClient, emailTemplateEndpoints.setDefault(id)),
  onSuccess: (_data, { id }, queryClient) => {
    invalidateLists(queryClient);
    invalidateDetail(queryClient, id);
    toast.success('Default email updated');
  },
});

export const useDeleteEmailTemplate = createMutationHook<{ deleted: true }, { id: string }>({
  mutationFn: ({ id }) =>
    apiDelete<{ deleted: true }>(operationsClient, emailTemplateEndpoints.delete(id)),
  onSuccess: (_data, _vars, queryClient) => {
    invalidateLists(queryClient);
    toast.success('Email deleted');
  },
});
